/**
 * Initials-fallback avatars.
 *
 * Used wherever a person is shown without a profile photo. Extracted from
 * `CommentComponent` so every surface that renders the same person renders the same
 * tint — a reactor bubble and that person's post avatar must not disagree.
 */

/**
 * Palette for the initials-fallback avatar. Fender-flavored — the accent hues used
 * elsewhere in-app plus complementary tones, so two users with the same initials
 * sitting next to each other don't collide.
 *
 * Order is LOAD-BEARING: `avatarColorFor()` picks by hashing the user id modulo the
 * palette length, so reshuffling this array changes which colour an existing user
 * gets. Append, don't reorder.
 */
export const AVATAR_COLOR_PALETTE: readonly string[] = [
  "#d52b1e", // fender red
  "#ff7a1a", // amp orange
  "#f0c419", // pickup gold
  "#4bb543", // stage green
  "#1f7a8c", // teal
  "#3b6ea8", // blues blue
  "#7c4bd0", // psych purple
  "#c93b8e", // magenta
  "#8a5a44", // rosewood
  "#5c5c5c", // slate
];

const FALLBACK_COLOR = "#5c5c5c";

/**
 * First + last initial, uppercased. Falls back to "?" when there's no usable name —
 * which happens for imported Discord ghosts whose display name we couldn't resolve.
 */
export function initialsFor(name: string | null | undefined): string {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Deterministic colour for a user id, so the same person keeps the same tint across
 * sessions, re-renders, and components.
 */
export function avatarColorFor(id: string | number | null | undefined): string {
  const str = String(id ?? "");
  if (!str) return FALLBACK_COLOR;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLOR_PALETTE[hash % AVATAR_COLOR_PALETTE.length];
}
